import type { CSSProperties } from 'react'
import { historyNatureToken, type BookingHistoryEntry } from '@/entities/booking'
import { SectionCard } from '@/shared/ui'
import s from './BookingHistoryCard.module.css'

type BookingHistoryCardProps = {
  entries: ReadonlyArray<BookingHistoryEntry>
}

/**
 * «Что уже делали» (prototype/Prototype.dc.html:2349) — ручные действия по брони с причинами.
 *
 * ⚠️ Ради этой секции спрашивалось «почему» во всех остальных. Спека называет вещи прямо:
 * «менеджер пишет пояснение, которое никто никогда не увидит» (3.2). Сервер требовал причину,
 * интерфейс её собирал — и она уходила в никуда. Здесь она наконец читается, поэтому `why`
 * набран не мелким серым хвостом, а цитатой: это единственное место, где видно, ЗАЧЕМ бронь
 * повели руками.
 *
 * Здесь только РУЧНОЕ. Как бронь шла по сценарию, показывает дорожка хода: смешав автоматические
 * шаги с решениями человека, мы получили бы журнал движка, в котором ищут как раз не его.
 */
export function BookingHistoryCard({ entries }: BookingHistoryCardProps) {
  return (
    <SectionCard className={s.card} eyebrow="Что уже делали">
      <div className={s.body}>
        {/* Не «каждое действие — с причиной»: `why` необязательно, и у записи, сделанной системой,
            его нет. Обещать причину у каждой строки значило бы соврать строкой ниже. */}
        <p className={s.lead}>Ручные действия по этой брони — и то, почему их сделали.</p>

        {/* `role="list"` при `list-style: none` не избыточен: Safari с VoiceOver снимает со списка
            семантику ровно из-за снятых маркеров, а этот список для того и существует, чтобы его
            читали строка за строкой. */}
        <ol className={s.list} role="list">
          {entries.map((entry) => (
            <li key={entry.id} className={s.entry}>
              <span
                className={s.dot}
                style={{ '--tint': historyNatureToken(entry.nature) } as CSSProperties}
                aria-hidden="true"
              />
              <span className={s.text}>
                <span className={s.what}>{entry.what}</span>
                {/* Причины может не быть у записи, сделанной системой, — придумывать её нельзя. */}
                {entry.why ? <q className={s.why}>{entry.why}</q> : null}
              </span>
              <span className={s.meta}>
                <span className={s.who}>{entry.who}</span>
                {/* `datetime` — машинное время из контракта: показываем готовую подпись сервера, а
                    скринридер и браузер получают разбираемую дату. */}
                <time className={s.when} dateTime={entry.at}>
                  {entry.atLabel}
                </time>
              </span>
            </li>
          ))}
        </ol>
      </div>
    </SectionCard>
  )
}
